import React from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { useCart } from "../../state/cartContext.jsx";
import { useAuth } from "../../state/authContext";
import { getNavbarDashboardLink } from "../../lib/dashboardRoutes";

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const getSupabaseStorageKey = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) return null;
  try {
    const { hostname } = new URL(url);
    const ref = hostname.split(".")[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch (_) {
    return null;
  }
};

const getCachedAccessToken = () => {
  if (typeof localStorage === "undefined") return null;
  const key = getSupabaseStorageKey();
  if (!key) return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.currentSession?.access_token || null;
  } catch (_) {
    return null;
  }
};

const getCachedSessionUserRole = () => {
  if (typeof localStorage === "undefined") return null;
  const key = getSupabaseStorageKey();
  if (!key) return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const user = parsed?.user || parsed?.currentSession?.user || null;
    return user?.app_metadata?.role || user?.user_metadata?.role || null;
  } catch (_) {
    return null;
  }
};

const normalizeRole = (value) => {
  if (!value || value === "authenticated") return null;
  return value;
};

export function SiteLayout({ children }) {
  const { cart } = useCart();
  const { user, role, groupMemberships, loading, signOut } = useAuth();

  const navigate = useNavigate();

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  const handleLogout = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  // Only show dashboard link when user is logged in;
  // fall back to JWT/metadata role if context role is null.
  const tokenPayload = decodeJwtPayload(getCachedAccessToken());
  const tokenRole =
    tokenPayload?.role ||
    tokenPayload?.app_metadata?.role ||
    tokenPayload?.user_metadata?.role ||
    null;
  const cachedUserRole = getCachedSessionUserRole();
  const effectiveRole =
    normalizeRole(role) ??
    normalizeRole(tokenRole) ??
    normalizeRole(cachedUserRole) ??
    normalizeRole(user?.app_metadata?.role) ??
    normalizeRole(user?.user_metadata?.role) ??
    null;
  const dashboardPath = user ? getNavbarDashboardLink({ role: effectiveRole, groupMemberships }) : null;
  const fallbackDashboardPath =
    user && !dashboardPath && !loading && effectiveRole === null ? "/admin" : null;
  const resolvedDashboardPath = dashboardPath || fallbackDashboardPath;

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 flex flex-col">
      <Navbar
        cartCount={cartCount}
        user={user}
        role={role}
        dashboardLink={resolvedDashboardPath}
        onLogout={handleLogout}
      />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
